import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout/Layout';
import StatCard from '../components/Dashboard/StatCard';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';

const API_BASE = process.env.REACT_APP_API_URL || '';

function getAuthHeaders() {
  const token = localStorage.getItem('travel-futura-token') || localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function apiFetch(url) {
  const res = await fetch(`${API_BASE}${url}`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

function fmtDate(d) {
  return d.toISOString().slice(0, 10);
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

const PRESETS = [
  { label: 'Today', from: () => fmtDate(new Date()), to: () => fmtDate(new Date()) },
  { label: 'Last 7 days', from: () => fmtDate(daysAgo(6)), to: () => fmtDate(new Date()) },
  { label: 'Last 30 days', from: () => fmtDate(daysAgo(29)), to: () => fmtDate(new Date()) },
  { label: 'Last 90 days', from: () => fmtDate(daysAgo(89)), to: () => fmtDate(new Date()) },
];

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#00f2fe', '#43e97b', '#fa709a', '#fee140', '#a18cd1', '#fbc2eb', '#ffecd2', '#fcb69f'];
const cardStyle = { background: '#fff', borderRadius: '8px', padding: '20px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };

const StampbookAnalytics = () => {
  const [dateFrom, setDateFrom] = useState(fmtDate(daysAgo(29)));
  const [dateTo, setDateTo] = useState(fmtDate(new Date()));
  const [activePreset, setActivePreset] = useState('Last 30 days');

  const [overview, setOverview] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [timeseriesData, setTimeseriesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = `from=${dateFrom}&to=${dateTo}`;
      const [ov, fn, ts] = await Promise.all([
        apiFetch(`/api/analytics/stampbook/overview?${qs}`),
        apiFetch(`/api/analytics/stampbook/completion-funnel?${qs}`),
        apiFetch(`/api/analytics/stampbook/timeseries?${qs}&interval=day`),
      ]);
      setOverview(ov);
      setFunnelData(fn);
      setTimeseriesData(ts);
    } catch (err) {
      console.error('Stampbook analytics fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handlePreset = (preset) => {
    setActivePreset(preset.label);
    setDateFrom(preset.from());
    setDateTo(preset.to());
  };

  const handleCustomFrom = (e) => { setActivePreset('Custom'); setDateFrom(e.target.value); };
  const handleCustomTo = (e) => { setActivePreset('Custom'); setDateTo(e.target.value); };

  // Prepare per-slot bar chart data (sorted DESC by unlock_count)
  const slotChartData = overview?.slots
    ? [...overview.slots].sort((a, b) => b.unlock_count - a.unlock_count)
    : [];

  // Prepare funnel chart data
  const funnelChartData = funnelData?.funnel || [];

  // Prepare timeseries chart data
  const tsChartData = timeseriesData?.series || [];

  return (
    <Layout title="Stampbook Analytics">
      <div style={{ padding: '20px' }}>
        {/* Date range filter */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '20px' }}>
          {PRESETS.map(p => (
            <button
              key={p.label}
              onClick={() => handlePreset(p)}
              style={{
                padding: '6px 14px',
                borderRadius: '6px',
                border: activePreset === p.label ? '2px solid #667eea' : '1px solid #ddd',
                background: activePreset === p.label ? '#667eea' : '#fff',
                color: activePreset === p.label ? '#fff' : '#333',
                cursor: 'pointer',
                fontWeight: activePreset === p.label ? '600' : '400',
              }}
            >
              {p.label}
            </button>
          ))}
          <span style={{ color: '#888', margin: '0 4px' }}>|</span>
          <input type="date" value={dateFrom} onChange={handleCustomFrom}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd' }} />
          <span style={{ color: '#888' }}>to</span>
          <input type="date" value={dateTo} onChange={handleCustomTo}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd' }} />
        </div>

        {error && (
          <div style={{ padding: '12px', background: '#fee', border: '1px solid #fcc', borderRadius: '8px', marginBottom: '16px', color: '#c33' }}>
            Error loading stampbook analytics: {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>Loading stampbook analytics...</div>
        ) : (
          <>
            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <StatCard title="Total Unique Users" value={(overview?.total_unique_users || 0).toLocaleString()} icon="U" color="blue" />
              <StatCard title="Total Unlocks" value={(overview?.total_unlocks || 0).toLocaleString()} icon="S" color="green" />
              <StatCard title="Active Slots" value={(overview?.active_slots || 0).toLocaleString()} icon="A" color="purple" />
              <StatCard title="Completed Users" value={(funnelData?.completed_users || 0).toLocaleString()} icon="C" color="orange" />
            </div>

            {/* Per-slot unlock bar chart */}
            {slotChartData.length > 0 && (
              <div style={cardStyle}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Per-Slot Unlock Counts</h3>
                <ResponsiveContainer width="100%" height={Math.max(250, slotChartData.length * 40)}>
                  <BarChart data={slotChartData} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis
                      dataKey="display_name"
                      type="category"
                      width={160}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(v) => v || 'Unnamed'}
                    />
                    <Tooltip
                      formatter={(val, name, props) => {
                        if (name === 'unlock_count') {
                          const pct = props.payload.unlock_pct;
                          return [`${val} unlocks (${Math.round(pct * 100)}%)`, 'Unlocks'];
                        }
                        return [val, name];
                      }}
                    />
                    <Bar dataKey="unlock_count" radius={[0, 4, 4, 0]}>
                      {slotChartData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Completion Funnel */}
            {funnelChartData.length > 0 && (
              <div style={cardStyle}>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600' }}>Completion Funnel</h3>
                <p style={{ color: '#888', fontSize: '13px', marginBottom: '16px' }}>
                  Distribution of users by stamp count
                </p>
                <ResponsiveContainer width="100%" height={Math.max(200, funnelChartData.length * 40)}>
                  <BarChart data={funnelChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} label={{ value: 'Users', position: 'insideBottom', offset: -5 }} />
                    <YAxis
                      dataKey="stamp_count"
                      type="category"
                      width={120}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(v) => `${v} stamp${v === 1 ? '' : 's'}`}
                    />
                    <Tooltip formatter={(val) => [`${val} users`, 'Users']} />
                    <Bar dataKey="user_count" radius={[0, 4, 4, 0]}>
                      {funnelChartData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Stamps over time */}
            {tsChartData.length > 0 && (
              <div style={cardStyle}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Stamps Over Time</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={tsChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="unlock_count" name="Unlocks" stroke="#667eea" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="unique_users" name="Unique Users" stroke="#43e97b" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Per-slot table (detailed) */}
            {slotChartData.length > 0 && (
              <div style={cardStyle}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Slot Details</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                      <th style={{ padding: '10px 12px', fontSize: '13px', color: '#666' }}>Slot</th>
                      <th style={{ padding: '10px 12px', fontSize: '13px', color: '#666' }}>Attraction</th>
                      <th style={{ padding: '10px 12px', fontSize: '13px', color: '#666' }}>Status</th>
                      <th style={{ padding: '10px 12px', fontSize: '13px', color: '#666', textAlign: 'right' }}>Unlocks</th>
                      <th style={{ padding: '10px 12px', fontSize: '13px', color: '#666', textAlign: 'right' }}>% of Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slotChartData.map((slot) => (
                      <tr key={slot.stamp_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '10px 12px', fontSize: '14px' }}>
                          {slot.icon || '📍'} {slot.display_name || `Slot ${slot.stamp_id}`}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '14px', color: slot.attraction_name ? '#333' : '#aaa' }}>
                          {slot.attraction_name || 'Unassigned'}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                            background: slot.is_active ? '#d4edda' : '#f8d7da',
                            color: slot.is_active ? '#155724' : '#721c24',
                          }}>
                            {slot.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '14px', textAlign: 'right', fontWeight: '600' }}>
                          {slot.unlock_count.toLocaleString()}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '14px', textAlign: 'right', color: '#888' }}>
                          {Math.round(slot.unlock_pct * 100)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default StampbookAnalytics;
